// Toasts: short confirmations with Undo and optional follow-up chips
// ("ate all / some / none"). Logging must be one tap; details are optional.

import type { LucideIcon } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { X } from 'lucide-react';

export interface ToastAction {
  label: string;
  onClick: () => void;
  /** Keep the toast open after this action. */
  keep?: boolean;
  primary?: boolean;
}

export interface Toast {
  id: number;
  message: string;
  detail?: string;
  icon?: LucideIcon;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'accent';
  actions?: ToastAction[];
  duration?: number;
}

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

export function toast(t: Omit<Toast, 'id'>): number {
  const id = nextId++;
  toasts = [...toasts.slice(-2), { ...t, id }];
  emit();
  timers.set(
    id,
    setTimeout(() => dismiss(id), t.duration ?? (t.actions?.length ? 8000 : 4000)),
  );
  return id;
}

export function dismiss(id: number) {
  clearTimeout(timers.get(id));
  timers.delete(id);
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function ToastHost() {
  const list = useSyncExternalStore(subscribe, () => toasts);
  return (
    <div className="toast-host" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`toast tone-${t.tone ?? 'neutral'}`}>
          {t.icon && <t.icon className="toast-icon" aria-hidden />}
          <div className="toast-text">
            <strong>{t.message}</strong>
            {t.detail && <span>{t.detail}</span>}
          </div>
          {t.actions && (
            <div className="toast-actions">
              {t.actions.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  className={a.primary ? 'primary' : ''}
                  onClick={() => {
                    a.onClick();
                    if (!a.keep) dismiss(t.id);
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
            <X aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}
